import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ShieldCheck, LayoutDashboard, LogOut, Plane, CreditCard, ChevronDown, Store, Heart, BadgeCheck, Home, UserRound, Search, Globe2, Smartphone } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { SITE } from "../lib/site";
import SearchBar from "./SearchBar";
import NotificationBell from "./NotificationBell";
import LanguageSwitcher from "./LanguageSwitcher";
import BrandMark from "./BrandMark";
import NavListenButton from "./NavListenButton";
import DensityTuner from "./DensityTuner";

// Navigation matching the TravelPulse design: Home, Explore, Travel, Stay, Community, My Travel Hub, News, Deals
const NAV = [
  { label: "Home", to: "/", icon: true },
  {
    label: "Explore",
    items: [
      ["About Traveler Pulse™", "/about"],
      ["Explore Global Places", "/explore-places"],
      ["Top 500 Destinations", "/top-destinations"],
      ["India City Guides", "/india-cities"],
      ["US City Guides", "/us-cities"],
      ["UK City Guides", "/uk-cities"],
      ["UAE City Guides", "/uae-cities"],
      ["Australia City Guides", "/au-cities"],
      ["World City Guides (Asia & Europe)", "/city-guides"],
      ["Flight Smart Search", "/flight-search"],
      ["Destinations", "/destinations"],
      ["Airports", "/airports"],
      ["Airport Marketplace", "/marketplace"],
      ["All Airports A–Z", "/airports/directory"],
      ["Indian Airports", "/india/airports"],
      ["World's Busiest Airports", "/airports/ranking"],
      ["Airlines", "/airlines"],
      ["All Airlines A–Z", "/airlines/directory"],
      ["Global Airline Ranking", "/airlines/ranking"],
      ["Aircraft", "/aircraft"],
      ["Countries", "/countries"],
      ["Route Map", "/map"],
    ],
  },
  {
    label: "Travel",
    items: [
      ["Flight Recovery (free)", "/recovery"],
      ["Price Watch", "/price-watch"],
      ["Travel Guides", "/guides"],
      ["Articles", "/articles"],
      ["Air Travel", "/air"],
      ["Rail Travel", "/rail"],
      ["Bus & Coach", "/bus"],
      ["Ground Transport", "/ground"],
      ["Ride Share", "/rideshare"],
      ["Road Travel", "/road"],
      ["Water Travel", "/water"],
      ["Visas & Embassies", "/visas"],
      ["Baggage Fees & Rules", "/baggage"],
      ["Fare Class Comparison", "/fares"],
      ["Pet Travel Checklist", "/pet-travel"],
      ["Packing Checklist", "/packing"],
      ["FAQ — Traveler Questions", "/faq"],
      ["Loyalty / Miles", "/loyalty"],
    ],
  },
  {
    label: "Plan a Trip",
    items: [
      ["AI Trip Planner", "/plan-ai"],
      ["My Travel Hub", "/travel-hub"],
      ["GroupTrip Planner", "/grouptrip-planner"],
      ["My Trip Today", "/trip-today"],
      ["Flight Smart Search", "/flight-search"],
      ["Packing Checklist", "/packing"],
      ["MTH — What & Why?", "/my-travel-hub-guide"],
      ["Get the App", "/get-app"],
    ],
  },
  {
    label: "Expenses",
    items: [
      ["Trip Expense Manager", "/trip-expense-manager"],
      ["Family Expense Hub", "/family-expense-hub"],
      ["Everyday Expenses", "/expenses"],
      ["Loyalty / Miles & Deals", "/loyalty"],
      ["Membership Plans", "/membership"],
    ],
  },
  { label: "Stay / Hotels", to: "/stays" },
  {
    label: "Community",
    items: [
      ["Community Wall", "/community"],
      ["Complaints", "/complaints"],
      ["Traveler Stories", "/specials"],
      ["FAQ — Traveler Questions", "/faq"],
    ],
  },
  { label: "My Travel Hub", to: "/travel-hub", highlight: true },
  {
    label: "News",
    items: [
      ["Travel News", "/travel-news"],
      ["Articles & Guides", "/articles"],
      ["My Deal Alerts", "/my-deal-alerts"],
    ],
  },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const [menu, setMenu] = useState(false);
  const [dropdown, setDropdown] = useState(null);
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/85 border-b border-border safe-t">
      {/* Account / auth utility row — its own line so the main nav never overflows the page */}
      <div className="hidden lg:block bg-secondary/60 border-b border-border" data-testid="nav-utility-row">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-11 flex items-center justify-end gap-2">
          <Link to="/support" data-testid="nav-support" title="Support TravelerPulse"
            className="inline-flex items-center gap-1 px-2 py-1 text-sm font-medium text-rose-600 hover:text-rose-500 transition-colors mr-auto">
            <Heart className="w-4 h-4 fill-rose-600" /> Support
          </Link>
          <NavListenButton compact />
          <DensityTuner testid="nav-density" compact />
          <LanguageSwitcher />
          {user ? (
            <>
            <NotificationBell />
            <div className="relative">
              <button onClick={() => setMenu(!menu)} data-testid="nav-user-menu"
                className="flex items-center gap-2 px-3 py-1 border border-zinc-300 hover:border-primary transition-colors bg-white">
                <div className="relative w-6 h-6 bg-primary text-white text-xs flex items-center justify-center font-mono rounded-full shrink-0">
                  {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || "U"}
                  <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-emerald-500 rounded-full border border-white" title="Signed in" />
                </div>
                <span className="text-sm max-w-[180px] truncate font-medium" data-testid="nav-user-name">{user.name || user.email?.split("@")[0]}</span>
                <ChevronDown className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
              </button>
              {menu && (
                <div className="absolute right-0 mt-1 w-56 bg-white border border-zinc-300 shadow-xl" onMouseLeave={() => setMenu(false)}>
                  <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50" data-testid="menu-user-header">
                    <div className="text-sm font-700 truncate">{user.name || "Signed in"}</div>
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    {["moderator", "admin"].includes(user.role) && (
                      <span className="inline-block mt-1 text-[10px] font-900 tracking-widest bg-violet-100 text-violet-700 px-1.5 py-0.5">ADMIN</span>
                    )}
                  </div>
                  <MenuItem icon={<Plane className="w-4 h-4 -rotate-45" />} label="My Travel Hub" onClick={() => { setMenu(false); nav("/travel-hub"); }} testid="menu-travel-hub" />
                  <MenuItem icon={<LayoutDashboard className="w-4 h-4" />} label="My Social Pulse" onClick={() => { setMenu(false); nav("/my-social-pulse"); }} testid="menu-social-pulse" />
                  <MenuItem icon={<UserRound className="w-4 h-4" />} label="Profile Settings" onClick={() => { setMenu(false); nav("/profile"); }} testid="menu-profile" />
                  <MenuItem icon={<ShieldCheck className="w-4 h-4" />} label="Privacy & Security" onClick={() => { setMenu(false); nav("/privacy-security"); }} testid="menu-privacy" />
                  <MenuItem icon={<BadgeCheck className="w-4 h-4" />} label="My Membership" onClick={() => { setMenu(false); nav("/membership"); }} testid="menu-membership" />
                  <MenuItem icon={<LayoutDashboard className="w-4 h-4" />} label="My Complaints" onClick={() => { setMenu(false); nav("/dashboard"); }} testid="menu-dashboard" />
                  <MenuItem icon={<Store className="w-4 h-4" />} label="My Airport Business" onClick={() => { setMenu(false); nav("/my-business"); }} testid="menu-my-business" />
                  {["moderator", "admin"].includes(user.role) && (
                    <MenuItem icon={<ShieldCheck className="w-4 h-4" />} label="Moderation" onClick={() => { setMenu(false); nav("/moderation"); }} testid="menu-moderation" />
                  )}
                  {["moderator", "admin"].includes(user.role) && (
                    <MenuItem icon={<LayoutDashboard className="w-4 h-4" />} label="Feed Source Health" onClick={() => { setMenu(false); nav("/admin/pulse-sources"); }} testid="menu-pulse-sources" />
                  )}
                  {["moderator", "admin"].includes(user.role) && (
                    <>
                      <MenuItem icon={<CreditCard className="w-4 h-4" />} label="Card & Affiliate Manager" onClick={() => { setMenu(false); nav("/admin/affiliates"); }} testid="menu-affiliates" />
                      <MenuItem icon={<LayoutDashboard className="w-4 h-4" />} label="IA Ref Traffic" onClick={() => { setMenu(false); nav("/admin/ref-traffic"); }} testid="menu-ref-traffic" />
                    </>
                  )}
                  {["moderator", "admin"].includes(user.role) && (
                    <MenuItem icon={<Store className="w-4 h-4" />} label="Travel Commerce" onClick={() => { setMenu(false); nav("/admin/travel"); }} testid="menu-travel-commerce" />
                  )}
                  {["moderator", "admin"].includes(user.role) && (
                    <MenuItem icon={<Globe2 className="w-4 h-4" />} label="Geo Content Engine" onClick={() => { setMenu(false); nav("/admin/geo"); }} testid="menu-geo-content" />
                  )}
                  {["moderator", "admin"].includes(user.role) && (
                    <MenuItem icon={<ShieldCheck className="w-4 h-4" />} label="Official Requests" onClick={() => { setMenu(false); nav("/admin/officials"); }} testid="menu-officials" />
                  )}
                  {["moderator", "admin"].includes(user.role) && (
                    <MenuItem icon={<CreditCard className="w-4 h-4" />} label="Membership Coupons" onClick={() => { setMenu(false); nav("/admin/coupons"); }} testid="menu-coupons" />
                  )}
                  {["moderator", "admin"].includes(user.role) && (
                    <MenuItem icon={<CreditCard className="w-4 h-4" />} label="Fare Database" onClick={() => { setMenu(false); nav("/admin/fares"); }} testid="menu-fares-admin" />
                  )}
                  {["moderator", "admin"].includes(user.role) && (
                    <MenuItem icon={<ShieldCheck className="w-4 h-4" />} label="Feedback Inbox" onClick={() => { setMenu(false); nav("/admin/feedback"); }} testid="menu-feedback-inbox" />
                  )}
                  {["moderator", "admin"].includes(user.role) && (
                    <MenuItem icon={<ShieldCheck className="w-4 h-4" />} label="Legal Consent" onClick={() => { setMenu(false); nav("/admin/consent"); }} testid="menu-legal-consent" />
                  )}
                  {["moderator", "admin"].includes(user.role) && (
                    <MenuItem icon={<ShieldCheck className="w-4 h-4" />} label="Listen Analytics" onClick={() => { setMenu(false); nav("/admin/listen"); }} testid="menu-listen-analytics" />
                  )}
                  {["moderator", "admin"].includes(user.role) && (
                    <MenuItem icon={<ShieldCheck className="w-4 h-4" />} label="Advertising Inquiries" onClick={() => { setMenu(false); nav("/admin/advertising"); }} testid="menu-advertising-inquiries" />
                  )}
                  {["moderator", "admin"].includes(user.role) && (
                    <MenuItem icon={<ShieldCheck className="w-4 h-4" />} label="Curated Answers" onClick={() => { setMenu(false); nav("/admin/answers"); }} testid="menu-curated-answers" />
                  )}
                  <MenuItem icon={<LogOut className="w-4 h-4" />} label="Sign out" onClick={async () => { setMenu(false); await logout(); nav("/"); }} testid="menu-logout" />
                </div>
              )}
            </div>
            <button onClick={async () => { setMenu(false); await logout(); nav("/"); }} data-testid="nav-signout"
              className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium text-zinc-600 border border-zinc-300 bg-white hover:border-primary hover:text-primary transition-colors">
              <LogOut className="w-4 h-4" /> Log out
            </button>
            </>
          ) : (
            <>
              <Link to="/install" data-testid="nav-install-app" title="Install TravelerPulse on iPhone or Android"
                className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-bold text-white bg-gradient-to-r from-emerald-600 to-emerald-500 hover:brightness-110 rounded-full shadow-sm whitespace-nowrap transition">
                <Smartphone className="w-3.5 h-3.5" /> Install App
              </Link>
              <Link to="/register" data-testid="nav-signup" className="px-4 py-1 text-sm font-bold text-primary border border-primary bg-white hover:bg-blue-50 transition-colors">Sign Up</Link>
              <Link to="/login" data-testid="nav-login" className="px-4 py-1 text-sm font-medium hover:text-primary transition-colors">Sign in</Link>
              <Link to="/complaints/new" data-testid="nav-file-complaint" className="px-4 py-1 text-sm font-semibold bg-primary text-white hover:bg-blue-700 transition-colors">File a complaint</Link>
            </>
          )}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-8">
        <div className="flex items-center gap-1.5 sm:gap-4 h-16 min-w-0 overflow-hidden">
          <div className="flex shrink-0 items-center overflow-visible" data-testid="nav-logo">
            <BrandMark size="md" subline to="/" testid="nav-brand-mark" />
          </div>

          <nav className="hidden lg:flex items-center gap-0.5 min-w-0 shrink">
            {NAV.map((item) => item.items ? (
              <div key={item.label} className="relative"
                onMouseEnter={() => setDropdown(item.label)} onMouseLeave={() => setDropdown(null)}>
                <button data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setDropdown(dropdown === item.label ? null : item.label)}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-zinc-700 hover:text-primary transition-colors whitespace-nowrap">
                  {item.label} <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {dropdown === item.label && (
                  <div className="absolute left-0 top-full z-50 w-56 max-h-[70vh] overflow-y-auto bg-white border border-zinc-300 shadow-xl" data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {item.items.map(([label, to]) => (
                      <Link key={label} to={to} onClick={() => setDropdown(null)}
                        data-testid={`nav-item-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="block px-4 py-2.5 text-sm hover:bg-primary hover:text-white transition-colors">
                        {label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : item.highlight ? (
              <Link key={item.label} to={item.to} data-testid="nav-my-travel-hub"
                className="flex items-center gap-1.5 mx-1.5 px-3.5 py-1.5 text-sm font-bold text-white rounded-full whitespace-nowrap bg-gradient-to-r from-[#0047FF] to-indigo-600 shadow-md hover:shadow-lg hover:brightness-110 transition-all">
                <Plane className="w-4 h-4 -rotate-45" /> {item.label}
              </Link>
            ) : (
              <Link key={item.label} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                title={item.icon ? "Home" : undefined} aria-label={item.icon ? "Home" : undefined}
                className="flex items-center px-3 py-2 text-sm font-medium text-zinc-700 hover:text-primary transition-colors whitespace-nowrap">
                {item.icon ? <Home className="w-4 h-4" /> : item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:block flex-1 max-w-xs ml-auto">
            <SearchBar compact />
          </div>

          <div className="lg:hidden ml-auto flex items-center gap-1 sm:gap-1.5 shrink-0">
            <Link to="/travel-hub" data-testid="nav-my-travel-hub-mobile" onClick={() => setOpen(false)}
              aria-label="My Travel Hub"
              title="My Travel Hub"
              className="inline-flex items-center gap-1 px-2 sm:px-2.5 py-1.5 text-[11px] sm:text-xs font-bold text-white rounded-full whitespace-nowrap bg-gradient-to-r from-[#0047FF] to-indigo-600 shadow-md active:scale-95 transition-transform">
              <Plane className="w-3.5 h-3.5 -rotate-45" />
              <span>My Hub</span>
            </Link>
            <button onClick={() => setMobileSearch((s) => !s)} data-testid="nav-mobile-search-toggle" aria-label="Search"
              className="hidden sm:inline-flex md:hidden p-1 text-zinc-600 hover:text-primary">
              <Search className="w-5 h-5" />
            </button>
            {user && <NotificationBell />}
            <Link to="/install" data-testid="nav-install-app-mobile" onClick={() => setOpen(false)}
              aria-label="Install app" title="Install TravelerPulse on your phone"
              className="hidden sm:inline-flex items-center gap-1 px-2 py-1.5 text-xs font-bold text-white rounded-full bg-gradient-to-r from-emerald-600 to-emerald-500 shadow-sm whitespace-nowrap active:scale-95 transition-transform">
              <Smartphone className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Install</span>
            </Link>
            {!user && (
              <Link to="/register" data-testid="nav-signup-mobile" onClick={() => setOpen(false)} className="hidden sm:inline-block px-2 py-1.5 text-xs font-bold text-primary border border-primary whitespace-nowrap">Sign Up</Link>
            )}
            <button onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu" className="p-1">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileSearch && !open && (
        <div className="md:hidden border-t border-zinc-200 bg-white px-5 py-2.5" data-testid="nav-mobile-search-row">
          <SearchBar compact />
        </div>
      )}

      {open && (
        <div className="lg:hidden border-t border-zinc-200 bg-white px-5 py-4 space-y-1 max-h-[75vh] overflow-y-auto">
          <div className="mb-3"><SearchBar compact /></div>
          {NAV.map((item) => item.items ? (
            <div key={item.label} className="py-1">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground py-1">{item.label}</div>
              {item.items.map(([label, to]) => (
                <Link key={label} to={to} onClick={() => setOpen(false)} className="block py-2 pl-3 text-sm font-medium">{label}</Link>
              ))}
            </div>
          ) : (
            <Link key={item.label} to={item.to} onClick={() => setOpen(false)} className="block py-2 text-sm font-medium">{item.label}</Link>
          ))}
          <div className="pt-3 border-t border-zinc-200">
            {user && (
              <div className="flex items-center gap-2 pb-2" data-testid="mobile-user-header">
                <div className="w-7 h-7 bg-primary text-white text-xs flex items-center justify-center font-mono rounded-full">{user.name?.[0]?.toUpperCase() || "U"}</div>
                <div className="min-w-0">
                  <div className="text-sm font-700 truncate">{user.name || "Signed in"}</div>
                  <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                </div>
              </div>
            )}
            <div className="flex gap-2">
            {user ? (
              <>
                <Link to="/travel-hub" onClick={() => setOpen(false)} className="flex-1 text-center py-2 bg-primary text-white text-sm">Travel Hub</Link>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="flex-1 text-center py-2 border border-zinc-300 text-sm">Complaints</Link>
                <button onClick={async () => { setOpen(false); await logout(); nav("/"); }} className="flex-1 py-2 bg-blue-700 text-white text-sm">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/register" onClick={() => setOpen(false)} className="flex-1 text-center py-2 border border-primary text-primary font-bold text-sm">Sign Up</Link>
                <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center py-2 border border-zinc-300 text-sm">Sign in</Link>
                <Link to="/complaints/new" onClick={() => setOpen(false)} className="flex-1 text-center py-2 bg-primary text-white text-sm">File complaint</Link>
              </>
            )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

const MenuItem = ({ icon, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-primary hover:text-white transition-colors">
    {icon}{label}
  </button>
);
